import { computed, ElementRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Store } from '@ngrx/store';
import { EMPTY } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { removePageErrorMessages, setPageErrorMessage, } from 'Angular/features/components/page-error-message/state/page-error-message.action';
import { PAGE_ERROR_MESSAGE_STATE_SLICE_KEY } from 'Angular/features/components/page-error-message/state/page-error-message.reducer';
import { DEFAULT_DISTRIBUTION_TYPE, DEFAULT_YEAR_LOOKBACK, FETCH_DEBOUNCE_MS, FUND_HISTORIC_DISTRIBUTION_CHART_OPTIONS, FUND_HISTORIC_DISTRIBUTION_CONFIGURABLE_OPTIONS, } from 'SecuritiesApp/ts/apps/income-data-app/constants';
import { IncomeDataService } from 'SecuritiesApp/ts/apps/income-data-app/services/income-data.service';
import { GeneralChartModule } from 'ChartApp/ts/features/general-chart/general-chart.module';
import * as i0 from "@angular/core";
import * as i1 from "@angular/forms";
import * as i2 from "../../../../../../../../charts/static/charts/ts/features/general-chart/components/general-chart/general-chart.component";
function FundHistoricDistributionsComponent_Conditional_40_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelement(0, "ycn-general-chart", 18);
} if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵproperty("configurableOptions", ctx_r0.configurableOptions)("chartOptions", ctx_r0.chartOptions)("chartJsonData", ctx);
} }
export class FundHistoricDistributionsComponent {
    constructor() {
        this.chartJsonData = signal(null, ...(ngDevMode ? [{ debugName: "chartJsonData" }] : []));
        this.customValue = signal(10000, ...(ngDevMode ? [{ debugName: "customValue" }] : []));
        this.type = signal(DEFAULT_DISTRIBUTION_TYPE, ...(ngDevMode ? [{ debugName: "type" }] : []));
        this.lookback = signal(DEFAULT_YEAR_LOOKBACK, ...(ngDevMode ? [{ debugName: "lookback" }] : []));
        this.hasError = signal(false, ...(ngDevMode ? [{ debugName: "hasError" }] : []));
        this._errorMessage = '';
        this._store = inject(Store);
        this._incomeDataService = inject(IncomeDataService);
        this._fund = inject(ElementRef).nativeElement.getAttribute('fund');
        this._params = computed(() => ({
            lookback: this.lookback(),
            type: this.type(),
            customValue: Number(this.customValue()),
        }), ...(ngDevMode ? [{ debugName: "_params" }] : []));
        this.configurableOptions = FUND_HISTORIC_DISTRIBUTION_CONFIGURABLE_OPTIONS;
        this.chartOptions = FUND_HISTORIC_DISTRIBUTION_CHART_OPTIONS;
        if (!window.USER?.authenticated)
            return;
        toObservable(this._params)
            .pipe(debounceTime(FETCH_DEBOUNCE_MS), distinctUntilChanged((previous, current) => previous.lookback === current.lookback &&
            previous.type === current.type &&
            previous.customValue === current.customValue), switchMap(({ lookback, type, customValue }) => this._incomeDataService.fetchFundHistoricDistribution(this._fund, lookback, type, customValue).pipe(catchError((errorResponse) => {
            this.handleError(errorResponse);
            return EMPTY;
        }))), takeUntilDestroyed())
            .subscribe((chartJsonData) => {
            this.clearError();
            this.chartJsonData.set(chartJsonData);
        });
    }
    handleError(errorResponse) {
        const plainTextError = typeof errorResponse.error === 'string' ? errorResponse.error : '';
        const errorMessage = errorResponse.error?.error_message || plainTextError || 'Something went wrong. Please try again.';
        this.clearError();
        this.hasError.set(true);
        this._errorMessage = errorMessage;
        this._store.dispatch(setPageErrorMessage({ message: errorMessage }));
    }
    clearError() {
        this.hasError.set(false);
        if (this._errorMessage) {
            this._store.dispatch(removePageErrorMessages({ messages: [this._errorMessage] }));
            this._errorMessage = '';
        }
    }
    setType(value) {
        this.type.set(value);
    }
    setLookback(value) {
        this.lookback.set(value);
    }
}
FundHistoricDistributionsComponent.ɵfac = function FundHistoricDistributionsComponent_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || FundHistoricDistributionsComponent)(); };
FundHistoricDistributionsComponent.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: FundHistoricDistributionsComponent, selectors: [["ycn-fund-historic-distributions"]], decls: 41, vars: 19, consts: [[1, "panel-header"], [1, "panel-title"], [1, "panel-actions"], [1, "panel-action"], [1, "nav", "nav-tabs"], [1, "nav-item"], ["href", "javascript:void(0)", 1, "nav-link", 3, "click"], [1, "chart-control", "chart-control-compact", "width-auto", "border-0"], [1, "chart-control-item"], ["href", "javascript:void(0)", 1, "chart-control-link", 3, "click"], [1, "panel-content"], [1, "d-flex", "align-items-center", "flex-wrap", "mb-2", "pt-2"], [1, "chart-legend-item-title", "bullet-1", "mr-20"], [1, "chart-legend-item-title", "bullet-2", "mr-20"], [1, "form-inline", "ml-auto"], [1, "text-thin", "mr-1", "mb-0"], ["type", "text", 1, "form-control", 3, "ngModelChange", "ngModel"], [1, "height-320"], [3, "configurableOptions", "chartOptions", "chartJsonData"]], template: function FundHistoricDistributionsComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵelementStart(0, "div", 0)(1, "h3", 1);
        i0.ɵɵtext(2, "Historic Distributions");
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(3, "div", 2)(4, "div", 3)(5, "ul", 4)(6, "div", 5)(7, "a", 6);
        i0.ɵɵlistener("click", function FundHistoricDistributionsComponent_Template_a_click_7_listener() { return ctx.setType("shares"); });
        i0.ɵɵtext(8, "Shares");
        i0.ɵɵelementEnd()();
        i0.ɵɵelementStart(9, "div", 5)(10, "a", 6);
        i0.ɵɵlistener("click", function FundHistoricDistributionsComponent_Template_a_click_10_listener() { return ctx.setType("dollars"); });
        i0.ɵɵtext(11, "Dollars");
        i0.ɵɵelementEnd()()()();
        i0.ɵɵelementStart(12, "div", 3)(13, "ul", 7)(14, "li", 8)(15, "a", 9);
        i0.ɵɵlistener("click", function FundHistoricDistributionsComponent_Template_a_click_15_listener() { return ctx.setLookback(3); });
        i0.ɵɵtext(16, "3Y");
        i0.ɵɵelementEnd()();
        i0.ɵɵelementStart(17, "li", 8)(18, "a", 9);
        i0.ɵɵlistener("click", function FundHistoricDistributionsComponent_Template_a_click_18_listener() { return ctx.setLookback(5); });
        i0.ɵɵtext(19, "5Y");
        i0.ɵɵelementEnd()();
        i0.ɵɵelementStart(20, "li", 8)(21, "a", 9);
        i0.ɵɵlistener("click", function FundHistoricDistributionsComponent_Template_a_click_21_listener() { return ctx.setLookback(10); });
        i0.ɵɵtext(22, "10Y");
        i0.ɵɵelementEnd()();
        i0.ɵɵelementStart(23, "li", 8)(24, "a", 9);
        i0.ɵɵlistener("click", function FundHistoricDistributionsComponent_Template_a_click_24_listener() { return ctx.setLookback(15); });
        i0.ɵɵtext(25, "15Y");
        i0.ɵɵelementEnd()();
        i0.ɵɵelementStart(26, "li", 8)(27, "a", 9);
        i0.ɵɵlistener("click", function FundHistoricDistributionsComponent_Template_a_click_27_listener() { return ctx.setLookback(null); });
        i0.ɵɵtext(28, "All");
        i0.ɵɵelementEnd()()()()()();
        i0.ɵɵelementStart(29, "div", 10)(30, "div", 11)(31, "span", 12);
        i0.ɵɵtext(32, "Income Dividend");
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(33, "span", 13);
        i0.ɵɵtext(34, "Capital Gains Distribution");
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(35, "div", 14)(36, "label", 15);
        i0.ɵɵtext(37);
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(38, "input", 16);
        i0.ɵɵlistener("ngModelChange", function FundHistoricDistributionsComponent_Template_input_ngModelChange_38_listener($event) { return ctx.customValue.set($event); });
        i0.ɵɵelementEnd()()();
        i0.ɵɵelementStart(39, "div", 17);
        i0.ɵɵconditionalCreate(40, FundHistoricDistributionsComponent_Conditional_40_Template, 1, 3, "ycn-general-chart", 18);
        i0.ɵɵelementEnd()();
    } if (rf & 2) {
        let tmp_10_0;
        i0.ɵɵadvance(7);
        i0.ɵɵclassProp("active", ctx.type() === "shares");
        i0.ɵɵadvance(3);
        i0.ɵɵclassProp("active", ctx.type() === "dollars");
        i0.ɵɵadvance(4);
        i0.ɵɵclassProp("current", ctx.lookback() === 3);
        i0.ɵɵadvance(3);
        i0.ɵɵclassProp("current", ctx.lookback() === 5);
        i0.ɵɵadvance(3);
        i0.ɵɵclassProp("current", ctx.lookback() === 10);
        i0.ɵɵadvance(3);
        i0.ɵɵclassProp("current", ctx.lookback() === 15);
        i0.ɵɵadvance(3);
        i0.ɵɵclassProp("current", ctx.lookback() === null);
        i0.ɵɵadvance(11);
        i0.ɵɵtextInterpolate1("Custom ", ctx.type());
        i0.ɵɵadvance();
        i0.ɵɵclassProp("is-invalid", ctx.hasError());
        i0.ɵɵproperty("ngModel", ctx.customValue());
        i0.ɵɵadvance(2);
        i0.ɵɵconditional((tmp_10_0 = ctx.chartJsonData()) ? 40 : -1, tmp_10_0);
    } }, dependencies: [FormsModule, i1.DefaultValueAccessor, i1.NgControlStatus, i1.NgModel, GeneralChartModule, i2.GeneralChartComponent], encapsulation: 2 });
